<template>
  <div @click="clicked">
    <editor-menu-bubble
      v-if="formatting"
      :editor="editor"
      @hide="hideLinkMenu"
      :keep-in-bounds="keepInBounds"
      v-slot="{ commands, isActive, getMarkAttrs, menu }"
    >
      <div
        v-if="linkMenuIsActive"
        class="dwp-absolute dwp-z-50 dwp-flex dwp-z-20 dwp-bg-gray-800 dwp-rounded dwp-p-2 dwp-mb-3 dwp-transition-250 dwp-transition-opacity"
        :class="{ 'dwp-opacity-100 dwp-visible': menu.isActive, 'dwp-invisible dwp-opacity-0': !menu.isActive }"
        :style="`left: ${menu.left}px; bottom: ${menu.top}px; transform: translateX(-50%);`"
      >
        <form
          class="dwp-flex dwp-items-center dwp-m-0"
          @submit.prevent="setLinkUrl(commands.link, linkUrl)"
        >
          <input
            class="menububble__input dwp-bg-transparent dwp-border-0 dwp-text-white focus:dwp-outline-none dwp-ml-2 dwp-inline-block"
            type="text"
            v-model="linkUrl"
            placeholder="https://"
            ref="linkInput"
            @keydown.esc="hideLinkMenu"
          />
          <button
            class="dwp-text-white dwp-p-2"
            @click.prevent="setLinkUrl(commands.link, null)"
            type="button"
          >
            <!--prettyhtml-ignore-->
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-x">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </form>
      </div>
      <template v-else>
        <div
          class="dwp-absolute dwp-z-50 dwp-flex dwp-bg-gray-800 dwp-rounded dwp-p-2 dwp-mb-3 dwp-transition-250 dwp-transition-opacity"
          :class="{ 'dwp-opacity-100 dwp-visible': menu.isActive, 'dwp-invisible dwp-opacity-0': !menu.isActive }"
          :style="`left: ${menu.left}px; bottom: ${menu.bottom}px; transform: translateX(-50%);`"
        >
          <EditorButton
            class="dwp-w-10"
            @mousedown.native.prevent="commands.bold"
            :active="isActive.bold()"
          >
            <!--prettyhtml-ignore-->
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
              <!--prettyhtml-ignore-->
              <path d="M16.5,9.5A3.5,3.5,0,0,0,13,6H8.5a1,1,0,0,0-1,1V17a1,1,0,0,0,1,1H13a3.49,3.49,0,0,0,2.44-6A3.5,3.5,0,0,0,16.5,9.5ZM13,16H9.5V13H13a1.5,1.5,0,0,1,0,3Zm0-5H9.5V8H13a1.5,1.5,0,0,1,0,3Z" />
            </svg>
          </EditorButton>

          <EditorButton
            class="dwp-w-10"
            :active="isActive.italic()"
            @mousedown.native.prevent="commands.italic"
          >
            <!--prettyhtml-ignore-->
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
              <!--prettyhtml-ignore-->
              <path d="M17,6H11a1,1,0,0,0,0,2h1.52l-3.2,8H7a1,1,0,0,0,0,2h6a1,1,0,0,0,0-2H11.48l3.2-8H17a1,1,0,0,0,0-2Z" />
            </svg>
          </EditorButton>

          <EditorButton
            class="dwp-w-10"
            :active="isActive.blockquote()"
            @mousedown.native.prevent="commands.blockquote"
          >
            <!--prettyhtml-ignore-->
            <svg viewBox="0 0 18 18" width="16" height="16" fill="currentColor" stroke="currentColor">
              <rect height="3" width="3" x="4" y="5" />
              <rect height="3" width="3" x="11" y="5" />
              <path d="M7,8c0,4.031-3,5-3,5" />
              <path d="M14,8c0,4.031-3,5-3,5" />
            </svg>
          </EditorButton>

          <EditorButton
            class="dwp-w-10"
            :active="isActive.code_block()"
            @mousedown.native.prevent="commands.code_block"
          >
            <!--prettyhtml-ignore-->
            <svg viewBox="0 0 18 18" width="16" height="16" stroke="currentColor" fill="currentColor">
              <polyline points="5 7 3 9 5 11" />
              <polyline points="13 7 15 9 13 11" />
              <line x1="10" x2="8" y1="5" y2="13" />
            </svg>
          </EditorButton>

          <EditorButton
            class="dwp-w-10"
            :active="isActive.link()"
            @mousedown.native.prevent="showLinkMenu(getMarkAttrs('link'))"
          >
            <!--prettyhtml-ignore-->
            <svg viewBox="0 0 18 18" width="16" height="16" stroke="currentColor" fill="none">
              <line class="dwp-stroke" x1="7" x2="11" y1="7" y2="11" />
              <!--prettyhtml-ignore-->
              <path class="dwp-even dwp-stroke" d="M8.9,4.577a3.476,3.476,0,0,1,.36,4.679A3.476,3.476,0,0,1,4.577,8.9C3.185,7.5,2.035,6.4,4.217,4.217S7.5,3.185,8.9,4.577Z" />
             <!--prettyhtml-ignore-->
              <path class="dwp-even dwp-stroke" d="M13.423,9.1a3.476,3.476,0,0,0-4.679-.36,3.476,3.476,0,0,0,.36,4.679c1.392,1.392,2.5,2.542,4.679.36S14.815,10.5,13.423,9.1Z" />
            </svg>
          </EditorButton>

          <EditorButton
            class="dwp-w-10"
            :active="isActive.ordered_list()"
            @mousedown.native.prevent="commands.ordered_list"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path
                d="M7.75 4.5h15a1 1 0 0 0 0-2h-15a1 1 0 0 0 0 2zm15 6.5h-15a1 1 0 1 0 0 2h15a1 1 0 0 0 0-2zm0 8.5h-15a1 1 0 0 0 0 2h15a1 1 0 0 0 0-2zM2.212 17.248a2 2 0 0 0-1.933 1.484.75.75 0 1 0 1.45.386.5.5 0 1 1 .483.63.75.75 0 1 0 0 1.5.5.5 0 1 1-.482.635.75.75 0 1 0-1.445.4 2 2 0 1 0 3.589-1.648.251.251 0 0 1 0-.278 2 2 0 0 0-1.662-3.111zm2.038-6.5a2 2 0 0 0-4 0 .75.75 0 0 0 1.5 0 .5.5 0 0 1 1 0 1.031 1.031 0 0 1-.227.645L.414 14.029A.75.75 0 0 0 1 15.248h2.5a.75.75 0 0 0 0-1.5h-.419a.249.249 0 0 1-.195-.406L3.7 12.33a2.544 2.544 0 0 0 .55-1.582zM4 5.248h-.25A.25.25 0 0 1 3.5 5V1.623A1.377 1.377 0 0 0 2.125.248H1.5a.75.75 0 0 0 0 1.5h.25A.25.25 0 0 1 2 2v3a.25.25 0 0 1-.25.25H1.5a.75.75 0 0 0 0 1.5H4a.75.75 0 0 0 0-1.5z"
              />
            </svg>
          </EditorButton>

          <EditorButton
            class="dwp-w-10"
            :active="isActive.bullet_list()"
            @mousedown.native.prevent="commands.bullet_list"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <circle cx="2.5" cy="3.998" r="2.5" />
              <path d="M8.5 5H23a1 1 0 0 0 0-2H8.5a1 1 0 0 0 0 2z" />
              <circle cx="2.5" cy="11.998" r="2.5" />
              <path d="M23 11H8.5a1 1 0 0 0 0 2H23a1 1 0 0 0 0-2z" />
              <circle cx="2.5" cy="19.998" r="2.5" />
              <path d="M23 19H8.5a1 1 0 0 0 0 2H23a1 1 0 0 0 0-2z" />
            </svg>
          </EditorButton>
        </div>
      </template>
    </editor-menu-bubble>

    <editor-content
      class="dwp-editor-content"
      :class="isMobile ? 'is-mobile' : ''"
      :editor="editor"
    />

    <div
      v-if="controls"
      class="dwp-editor__controls dwp-flex dwp-justify-between dwp-absolute dwp-bottom-0 dwp-w-full dwp-left-0 dwp-px-3 dwp-pb-2"
    >
      <div class="dwp-editor__controls--left dwp-flex dwp-items-center" ref="footer-left">
        <slot name="footer-left"></slot>
      </div>
      <div class="dwp-editor__controls--right dwp-flex dwp-items-center" ref="footer-right">
        <slot name="footer-right-before" />
        <slot name="footer-right" />
        <slot name="footer-right-after" />
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBubble } from "tiptap";
import EditorButton from "./EditorButton.vue";

import {
  Blockquote,
  BulletList,
  CodeBlock,
  HardBreak,
  Mention,
  ListItem,
  OrderedList,
  Bold,
  Code,
  Italic,
  Link,
  Image,
  Strike,
  Underline,
  History,
  Placeholder
} from "tiptap-extensions";

export default {
  props: {
    isMobile: {
      type: Boolean,
      default: false
    },
    formatting: {
      type: Boolean,
      default: true
    },
    placeholder: String,
    content: String,
    members: Array,
    default: String,
    controls: {
      type: Boolean,
      default: true
    }
  },

  components: {
    EditorContent,
    EditorButton,
    EditorMenuBubble
  },

  data() {
    return {
      text: "",
      focused: false,
      // controls: false,
      keepInBounds: true,
      editor: null,
      linkUrl: null,
      linkMenuIsActive: false,
      query: null,
      suggestion: {
        left: 100,
        bottom: 54
      },
      suggestionRange: null,
      filteredUsers: [],
      navigatedUserIndex: 0,
      insertMention: () => {}
    };
  },

  mounted() {
    if (this.default) {
      this.text = this.default;
    }

    if (this.content) {
      this.text = this.content;
    }

    this.createEditor();

    // programmatically mention
    // this.$event.$on("mention", () => {
    //   const transaction = this.editor.state.tr.insertText(" @");
    //   this.editor.view.dispatch(transaction);
    // });
  },

  beforeDestroy() {
    this.editor.destroy();
  },

  watch: {
    text(val) {
      this.$emit("update:content", val);
    },
    content(val, oldVal) {
      this.text = val;
      if (!val) {
        this.editor.setContent(val, true);
      }
    }
  },

  methods: {
    clicked() {
      if (!this.text) {
        this.focus("end");
      }
    },

    createEditor() {
      this.editor = new Editor({
        onUpdate: ({ state, getHTML, getJSON, transaction }) => {
          this.text = getHTML();
          this.$emit("input", this.text);
        },
        // fix a shitty firefox bug
        onFocus: ({ event, state, view }) => {
          if (!this.text || this.text == "<p></p>") {
            this.editor.clearContent(true);
          }
        },
        onBlur: args => {
          this.$emit("blur", args);
        },
        content: this.text,
        autoFocus: this.focused,
        useBuiltInExtensions: this.formatting,
        extensions:
          this.formatting === true
            ? [
                new Blockquote(),
                new BulletList(),
                new Image(),
                new CodeBlock(),
                new HardBreak(),
                new ListItem(),
                new OrderedList(),
                new Bold(),
                new Code(),
                new Italic(),
                new Link({ target: "_blank" }),
                new Underline(),
                new History(),
                new Placeholder({
                  emptyClass: "is-empty",
                  emptyNodeText: this.placeholder,
                  showOnlyWhenEditable: true
                })
              ]
            : [
                new History(),
                new Placeholder({
                  emptyClass: "is-empty",
                  emptyNodeText: this.placeholder,
                  showOnlyWhenEditable: true
                })
              ]
      });
    },

    showLinkMenu(attrs) {
      this.linkUrl = attrs.href;
      this.linkMenuIsActive = true;
      this.$nextTick(() => {
        this.$refs.linkInput.focus();
      });
    },

    hideLinkMenu() {
      this.linkUrl = null;
      this.linkMenuIsActive = false;
    },

    setLinkUrl(command, url) {
      command({ href: url });
      this.hideLinkMenu();
      this.editor.focus();
    },

    setContent(content) {
      this.editor.setContent(content, true);
      this.text = content;
    },

    blur() {
      this.editor.blur();
    },

    focus() {
      this.editor.focus("end");
    },

    clear() {
      this.text = "";
      // this.editor.clearContent(true);
    },

    reset() {
      this.editor.clearContent(true);
      this.editor.destroy();
      this.createEditor();
    }
  }
};
</script>